import React, { useEffect, useState, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import DOMPurify from "dompurify";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import {
  Box,
  CircularProgress,
  Modal,
  IconButton,
  MenuItem,
  InputLabel,
  Select,
  ListItemText,
  Checkbox,
  OutlinedInput,
  FormControl,
  Typography,
} from "@mui/material";

import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import CloseIcon from "@mui/icons-material/Close";

import {
  resetNotificationsService,
  resetUsers,
  getNotificationsUserList,
} from "store/reducers/usersSlice";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  style: {
    maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
    width: 250,
  },
};

const AddSms = ({ openModal, onCloseModal, onCreateSms, selectedSms }) => {
  const containerRef = useRef(null);
  const dispatch = useDispatch();
  const { notifications } = useSelector((state) => state.usersSlice);
  const { users } = useSelector((state) => state.usersSlice);

  const { notificationsUserListData: usersList, totalCount } = users;
  const [isLoading, setLoading] = useState(false);
  const [isFetchingPolicy] = useState(false);
  const [setdisableSaveBtn] = useState(true);
  const [tagName, setTagName] = useState([]);
  const [selectedCitizenshipStatus, setSelectedCitizenshipStatus] =
    useState("");
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isHasMoreData, setHasMoreData] = useState(true);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isApiCallSuccessful, setIsApiCallSuccessful] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const observerRef = useRef(
    new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setCurrentPage((previous) => previous + 1);
      }
    })
  );

  const ref = useCallback((node) => {
    observerRef.current?.disconnect();
    if (node) {
      observerRef.current.observe(node);
    }
  }, []);

  useEffect(
    () => () => {
      observerRef.current.disconnect();
    },
    []
  );

  const handleUserTypeChange = async (event) => {
    setSelectedCitizenshipStatus(event.target.value);
    setTagName([]);
    setCurrentPage(1);
    setIsLoadingData(false);
    setIsSelectAllChecked(false);
    dispatch(resetUsers());
    setHasMoreData(true);
    await dispatch(
      getNotificationsUserList({
        page: 1,
        limit: 10,
        citizenshipStatus: event.target.value,
      })
    );
  };

  const handleSelectAllChange = (event) => {
    const { checked } = event.target;
    setIsSelectAllChecked(checked);
    setTagName(checked ? usersList?.map((item) => item?.userId) : []);
  };

  const handleUserCheckboxLabelClick = (event) => {
    event.stopPropagation();
    const checkbox = event.target
      .closest("li")
      .querySelector('input[type="checkbox"]');
    checkbox.checked = !checkbox.checked;
    handleSelectAllChange({ target: checkbox });
  };

  const handleSelectTagNameChange = (event) => {
    const { value } = event.target;
    setTagName(value);
    setIsSelectAllChecked(value.length === usersList.length);
  };

  const renderSelectedValue = () => {
    if (tagName?.length === users?.length) {
      return "select all";
    }
    return tagName.map((userId) => {
      const user = usersList.find((item) => item.userId === userId);
      return user ? `${user.email} (${user.onboardingStage})` : "";
    });
  };

  const smsSchema = yup.object({
    body: yup
      .string()
      .trim()
      .required(`Sms body is required`)
      .min(2, "Minimum two character is required")
      .max(160, "Maximum 160 characters allowed"),
    userType: yup
      .string()
      .oneOf(
        [
          "All",
          "US",
          "Non-US",
          "Onboarding not started",
          "Incomplete-Onboarding",
          "Email Verified",
          "Pin Setup Done",
          "Identity Verification done",
          "Contact information added",
          "SSN and DOB added",
          "Accredited check completed",
          "Agreements accepted",
          "Onboarding completed",
        ],
        "Invalid UserType selection"
      )
      .required("UserType is required"),
    usersList: yup
      .array()
      .test({
        name: "usersListRequired",
        test: (value) => value && value.length > 0,
        message: "Please select at least one user.",
      })
      .nullable()
      .required("Users is required"),
  });

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
    reset,
    setValue,
    trigger,
  } = useForm({
    mode: "all",
    resolver: yupResolver(smsSchema),
  });

  useEffect(() => {
    // Watch the changes of selectedCitizenshipStatus (userType)
    if (selectedCitizenshipStatus) {
      // Trigger validation for the usersList field when userType changes
      setValue("usersList", null); // Reset the usersList value to null to trigger validation
      trigger("usersList");
    }
  }, [selectedCitizenshipStatus]);

  useEffect(() => {
    // If the usersList changes (new data is fetched), update the tagName state if "select all" is checked
    if (isSelectAllChecked && usersList.length > 0) {
      const newUsers = usersList.map((item) => item?.userId);
      // Check if any newly fetched users are not already in the tagName state
      const uniqueNewUsers = newUsers.filter(
        (userId) => !tagName.includes(userId)
      );
      setTagName((prevTags) => [...prevTags, ...uniqueNewUsers]);
    }
  }, [usersList]);

  useEffect(() => {
    const fetchUsers = async () => {
      // Fetch users data for the current page
      dispatch(
        getNotificationsUserList({
          page: currentPage,
          limit: 10,
          citizenshipStatus: selectedCitizenshipStatus,
        })
      )
        .unwrap()
        .then((data) => {
          if (data.data.data.currentPage > data.data.data.totalPage) {
            setHasMoreData(false);
          }
        });
      // Check if there is more data to fetch
      setHasMoreData(usersList.length < totalCount);
    };

    // Fetch initial user data
    if (
      selectedCitizenshipStatus &&
      !isLoadingData &&
      isHasMoreData &&
      currentPage != 1
    ) {
      setIsLoadingData(true);
      fetchUsers()
        .then(() => setIsLoadingData(false))
        .catch(() => setIsLoadingData(false));
    }
  }, [currentPage, selectedCitizenshipStatus]);

  useEffect(() => {
    if (notifications) {
      reset(
        {
          body: notifications?.body,
        },
        {
          keepIsValid: false,
          keepErrors: true,
          keepDirty: false,
        }
      );
    }
  }, [notifications]);

  useEffect(() => {
    return () => {
      dispatch(resetNotificationsService());
      dispatch(resetUsers());
    };
  }, []);

  const onAddSmsSubmit = async (data) => {
    try {
      if (isSending) {
        // If the SMS sending process is ongoing, don't do anything
        return;
      }
      setIsSending(true);
      setLoading(true);
      const cleanHtml = DOMPurify.sanitize(data?.body);
      const requestData = isSelectAllChecked
        ? { body: cleanHtml, citizenshipStatus: selectedCitizenshipStatus }
        : {
            body: cleanHtml,
            userId: tagName.map(
              (userId) =>
                usersList.find((item) => item.userId === userId).userId
            ),
          };
      await new Promise((resolve) => setTimeout(resolve, 2000));
      onCreateSms(requestData);
      setIsApiCallSuccessful(true);
    } catch (error) {
      toast.error("Something went wrong!");
    } finally {
      setLoading(false);
      setIsSending(false);
    }
  };

  const handleEdit = (data) => {
    if (`${selectedSms.body}` === `${data}`.trim()) {
      setdisableSaveBtn(true);
    } else {
      setdisableSaveBtn(false);
    }
  };

  const isSecondDropdownDisabled = () => {
    return selectedCitizenshipStatus === "";
  };

  const userTypeRegister = register("userType");
  return (
    <Modal
      open={openModal}
      onClose={(_, reason) => {
        if (reason === "backdropClick") return;
        onCloseModal();
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      disableEscapeKeyDown>
      <Box
        ref={containerRef}
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          boxShadow: 24,
          maxHeight: "calc(100vh - 100px)",
          width: "1200px",
          maxWidth: "calc(100vw - 48px)",
          overflow: "auto",
          p: 4,
          borderRadius: 3,
        }}>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center">
          <MDTypography id="modal-modal-title" variant="h4" component="h2">
            {"Add Sms"}
          </MDTypography>
          <IconButton onClick={onCloseModal}>
            <CloseIcon fontSize="large" />
          </IconButton>
        </MDBox>
        {isFetchingPolicy ? (
          <Box
            sx={{
              textAlign: "center",
              paddingTop: "32px",
              paddingBottom: "24px",
              height: "800px",
              display: "grid",
              placeItems: "center",
              maxHeight: "calc(100vh - 100px)",
            }}>
            <CircularProgress color="info" />
          </Box>
        ) : (
          <>
            <MDBox marginTop="32px">
              <div tyle={{ display: "flex", alignItems: "center" }}>
                <FormControl sx={{ m: 1, width: 300 }}>
                  <InputLabel id="demo-simple-select-label">
                    UserType
                  </InputLabel>
                  <Select
                    {...userTypeRegister}
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={selectedCitizenshipStatus}
                    label="UserType"
                    onChange={(e) => {
                      userTypeRegister.onChange(e);
                      handleUserTypeChange(e);
                    }}>
                    <MenuItem value={"All"}>All</MenuItem>
                    <MenuItem value={"US"}>US</MenuItem>
                    <MenuItem value={"Non-US"}>Non-US</MenuItem>
                    <MenuItem value={"Onboarding not started"}>
                      Onboarding not started
                    </MenuItem>
                    <MenuItem value={"Incomplete-Onboarding"}>
                      Incomplete-Onboarding
                    </MenuItem>
                    <MenuItem value={"Email Verified"}>Email Verified</MenuItem>
                    <MenuItem value={"Pin Setup Done"}>Pin Setup Done</MenuItem>
                    <MenuItem value={"Identity Verification done"}>
                      Identity Verification done
                    </MenuItem>
                    <MenuItem value={"Contact information added"}>
                      Contact information added
                    </MenuItem>
                    <MenuItem value={"SSN and DOB added"}>
                      SSN & DOB added
                    </MenuItem>
                    <MenuItem value={"Accredited check completed"}>
                      Accredited check completed
                    </MenuItem>
                    <MenuItem value={"Agreements accepted"}>
                      Agreements accepted
                    </MenuItem>
                    <MenuItem value={"Onboarding completed"}>
                      Onboarding completed
                    </MenuItem>
                  </Select>
                </FormControl>
                <FormControl sx={{ m: 1, width: 789 }}>
                  <InputLabel
                    id="demo-multiple-checkbox-label"
                    disabled={isSecondDropdownDisabled()}
                    sx={{
                      opacity: isSecondDropdownDisabled() ? 0.6 : 1,
                      backgroundColor: isSecondDropdownDisabled()
                        ? "#f0f0f0"
                        : "transparent",
                      pointerEvents: isSecondDropdownDisabled()
                        ? "none"
                        : "auto",
                    }}>
                    Users
                  </InputLabel>
                  <Select
                    {...register("usersList")}
                    labelId="demo-multiple-checkbox-label"
                    id="demo-multiple-checkbox"
                    multiple
                    value={tagName}
                    onChange={(event) => {
                      handleSelectTagNameChange(event);
                    }}
                    input={<OutlinedInput label="Users" />}
                    renderValue={() => renderSelectedValue()}
                    MenuProps={{
                      PaperProps: {
                        // onScroll: handleScroll, // Add the onScroll event
                        ...MenuProps,
                      },
                    }}
                    disabled={isSecondDropdownDisabled()}
                    sx={{
                      maxHeight: "150px",
                      overflowY: "auto",
                      "& > div": { height: "150px" },
                    }}>
                    {isLoading && (
                      <MenuItem>
                        <MDTypography
                          display="inline"
                          fontSize="14px"
                          color="text">
                          Loading...
                        </MDTypography>
                      </MenuItem>
                    )}
                    {/* "Select all" checkbox inside the Tag dropdown */}
                    {usersList?.length && (
                      <MenuItem
                        key="select all"
                        value="select all"
                        name="selectAll">
                        <Checkbox
                          checked={isSelectAllChecked}
                          onChange={handleSelectAllChange}
                        />
                        <ListItemText
                          onClick={(event) =>
                            handleUserCheckboxLabelClick(event)
                          }>
                          <Typography>Select All ({totalCount})</Typography>
                        </ListItemText>
                      </MenuItem>
                    )}

                    {usersList?.map((tag, index) => (
                      <MenuItem
                        sx={{ padding: "5px 0" }}
                        key={tag.userId}
                        value={tag.userId}
                        name={`${tag.email} ${tag.onboardingStage}`}
                        ref={index === usersList.length - 1 ? ref : undefined}>
                        <Checkbox
                          checked={tagName.includes(tag.userId)}
                          value={tag.userId}
                        />
                        <MDTypography
                          display="inline"
                          fontSize="14px"
                          color="text">
                          {`${tag.email} (${tag.onboardingStage})`}
                        </MDTypography>
                      </MenuItem>
                    ))}
                  </Select>
                  {errors?.usersList && (
                    <MDTypography color="error" variant="caption">
                      {errors?.usersList?.message}
                    </MDTypography>
                  )}
                </FormControl>
              </div>
              <MDTypography variant="h5" mb={2}>
                Body
              </MDTypography>
              <Controller
                name="body"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <MDInput
                    multiline
                    rows={7}
                    fullWidth
                    value={value}
                    onChange={onChange}
                    handleEdit={handleEdit}
                    style={{
                      height: "200px",
                      width: "100%",
                      marginTop: "16px",
                    }}
                  />
                )}
              />
              {errors?.body && (
                <MDTypography color="error" variant="caption">
                  {errors?.body?.message}
                </MDTypography>
              )}
            </MDBox>
            <MDBox align="right" marginTop="32px">
              <MDButton onClick={onCloseModal}>Discard</MDButton>
              <MDButton
                onClick={handleSubmit(onAddSmsSubmit)}
                color="info"
                disabled={
                  Object.keys(errors).length > 0 ||
                  !isValid ||
                  isSubmitting ||
                  isApiCallSuccessful
                }
                isLoading={isLoading}>
                Send
              </MDButton>
            </MDBox>
          </>
        )}
      </Box>
    </Modal>
  );
};

AddSms.propTypes = {
  openModal: PropTypes.bool,
  onCloseModal: PropTypes.func,
  isEdit: PropTypes.bool,
  onCreateSms: PropTypes.func,
  selectedSms: PropTypes.object,
};

AddSms.defaultProps = {
  openModal: false,
  onCloseModal: () => {},
  isEdit: false,
  onCreateSms: () => {},
  selectedSms: null,
};

export default AddSms;
